import React from 'react';
import { useParams } from 'react-router-dom';
import { useDetailQuery } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { ShopProvider } from 'context/shopContext';
import { ShopCategory, ShopListItem } from 'components/Types/Tenant/shop.type';
import { Spinner } from 'reactstrap';

// Simple UUID v4 regex (accepts lowercase, uppercase)
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const InvalidShopIdScreen: React.FC = () => (
  <div
    className="page-content d-flex align-items-center justify-content-center"
    style={{ height: '80vh' }}
  >
    <div className="text-center">
      <h2 className="text-danger mb-3">Invalid Shop ID</h2>
      <p>The shop identifier in the URL is not valid. Please check the link or contact support.</p>
    </div>
  </div>
);

const InvalidCategoryScreen: React.FC<{ category: string }> = ({ category }) => (
  <div
    className="page-content d-flex align-items-center justify-content-center"
    style={{ height: '80vh' }}
  >
    <div className="text-center">
      <h2 className="text-danger mb-3">Invalid Shop Category</h2>
      <p>
        The shop category <strong>{category}</strong> is invalid. Please check the link or contact
        support.
      </p>
    </div>
  </div>
);

const ShopWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { shopId } = useParams<{ shopId: string }>();

  if (!shopId || !UUID_REGEX.test(shopId)) {
    return <InvalidShopIdScreen />;
  }

  const {
    data: shopData,
    isLoading,
    isError,
  } = useDetailQuery<ShopListItem>(API_ENDPOINTS.TENANT.SHOPS.WITH_ID, {
    id: shopId,
  });

  if (isLoading) {
    return (
      <div
        className="page-content d-flex align-items-center justify-content-center"
        style={{ height: '80vh' }}
      >
        <div className="text-center">
          <Spinner color="primary" />
        </div>
      </div>
    );
  }

  if (isError || !shopData) {
    return (
      <div
        className="page-content d-flex align-items-center justify-content-center"
        style={{ height: '80vh' }}
      >
        <div className="text-center text-danger">
          <h4>Failed to load shop data. Please try again later.</h4>
        </div>
      </div>
    );
  }

  // Validate shop category
  if (!Object.values(ShopCategory).includes(shopData.category)) {
    return <InvalidCategoryScreen category={shopData.category} />;
  }

  return <ShopProvider shop={shopData}>{children}</ShopProvider>;
};

export default ShopWrapper;
